import type ServiceApi from "../../api";
import {RevaApi} from "../api-constants";
import {SimpleApiInterface} from "../api-interface";
import type {ApiCredentials} from "../credentials";
import {ANONYMOUS_CREDENTIALS} from "../credentials";
import type {ApiExecutor} from "../executor";
import type {
    EmailVerificationRedeemPayload,
    EmailVerificationRedeemResponse,
} from "../payload-types/email-verification";
import {WebError} from "../web-error";

/**
 * #19351: POST {@link RevaApi.PATH_EMAIL_VERIFICATION_REDEEM} — redeems an e-mail-verification token
 * together with username and password, proving that the caller controls both the login and the mailbox
 * the link was sent to. Needs no credentials (the whole point is authenticating via the payload, not a
 * session/JWT), so `execute` is overridden to always supply {@link ANONYMOUS_CREDENTIALS} — never the
 * caller's (possibly still valid, possibly stale) JWT.
 *
 * 422/429/503 are rethrown as the raw {@link WebError} instead of being converted to a generic `ErrorI18n`
 * (unlike the base class default): 422 carries account-management's own `error`/`error_de` message (link
 * invalid/expired/already used/for the wrong account, or wrong username/password), 429 is reva-rest's own
 * IP throttle, 503 means account-management is currently unreachable.
 */
export class EmailVerificationRedeemApiInterface extends SimpleApiInterface<EmailVerificationRedeemResponse> {
    constructor(private readonly payload: EmailVerificationRedeemPayload) {
        super();
    }

    async execute(api: ServiceApi): Promise<EmailVerificationRedeemResponse> {
        return super.execute(api, ANONYMOUS_CREDENTIALS);
    }

    protected buildUrl(api: ServiceApi): string {
        return api.buildRevaApiUrl([RevaApi.PATH_EMAIL_VERIFICATION_REDEEM]);
    }

    protected buildPayload(): EmailVerificationRedeemPayload {
        return this.payload;
    }

    protected executeInternal(
        executor: ApiExecutor,
        url: string,
        credentials: ApiCredentials,
    ): Promise<EmailVerificationRedeemResponse> {
        return executor.fetchPOST(url, credentials, this.buildPayload());
    }

    protected override handleApiErrors(e: Error): EmailVerificationRedeemResponse {
        if (e instanceof WebError && (e.status === 422 || e.status === 429 || e.status === 503)) {
            throw e;
        }
        return super.handleApiErrors(e);
    }
}
